import Joi from "joi-browser";

enum CompletionStatus {
  Completed = "completed",
  Failed = "failed",
  Paused = "paused",
  Cancelled = "cancelled",
}

enum EndNodeType {
  Default = "default",
  ArchiveConversation = "archive_conversation",
  WebhookNotification = "webhook_notification",
}

enum ArchiveReason {
  Resolved = "resolved",
  TimedOut = "timed_out",
  Manual = "manual",
  Error = "error",
  Other = "other",
}

interface DefaultEndNodeSettings {
  name: string;
  description: string;
  completionStatus: CompletionStatus;
  endNodeType: EndNodeType.Default;
}

type DefaultEndNode = {
  nodeType: "end";
  settings: DefaultEndNodeSettings;
};

interface ArchiveConversationNodeSettings {
  name: string;
  description: string;
  archiveReason: ArchiveReason;
  endNodeType: EndNodeType.ArchiveConversation;
  completionStatus: CompletionStatus;
}

type ArchiveConversationNode = {
  nodeType: "end";
  settings: ArchiveConversationNodeSettings;
};

type Webhook = {
  webhookName: string;
  endpointUrl: string;
  authRequired?: boolean;
  authKey?: string;
  createdAt?: number;
  createdBy: string;
};

interface WebhookNotificationNodeSettings {
  name: string;
  description: string;
  webhookList: Array<Webhook>;
  endNodeType: EndNodeType.WebhookNotification;
  maxRetries: number;
  retryDelayMs: number;
  completionStatus: CompletionStatus;
}

type WebhookNotificationNode = {
  nodeType: "end";
  settings: WebhookNotificationNodeSettings;
};

type EndNode =
  | DefaultEndNode
  | ArchiveConversationNode
  | WebhookNotificationNode;

interface EndNodeSettings {
  name: string;
  description: string;
  completionStatus: CompletionStatus;
  endNodeType: EndNodeType;
}

const defaultEndNodeSettings: DefaultEndNodeSettings = {
  name: "End",
  description: "",
  completionStatus: CompletionStatus.Completed,
  endNodeType: EndNodeType.Default,
};

const defaultEndNode: DefaultEndNode = {
  nodeType: "end",
  settings: defaultEndNodeSettings,
};

const endNodeSettingsTemplate = [
  {
    name: "name",
    label: "Name",
    type: "text",
  },
  {
    name: "description",
    label: "Description",
    type: "textArea",
    style: { height: "50px" },
  },
  // {
  //   name: "completionStatus",
  //   label: "Status",
  //   type: "select",
  //   options: [
  //     { id: 0, value: "", label: "(None selected)" },
  //     {
  //       id: 1,
  //       value: CompletionStatus.Completed,
  //       label: CompletionStatus.Completed,
  //     },
  //     {
  //       id: 2,
  //       value: CompletionStatus.Failed,
  //       label: CompletionStatus.Failed,
  //     },
  //     {
  //       id: 3,
  //       value: CompletionStatus.Cancelled,
  //       label: CompletionStatus.Cancelled,
  //     },
  //     {
  //       id: 4,
  //       value: CompletionStatus.Paused,
  //       label: CompletionStatus.Paused,
  //     },
  //   ],
  // },
];

const endNodeSettingsSchema = {
  name: Joi.string().required(),
  description: Joi.string().allow(null, ""),
  endNodeType: Joi.string().required(),
  completionStatus: Joi.string().required().label("Completion Status"),
};

const defaultArchiveConversationNode = {
  nodeType: "end",
  settings: {
    name: "Archive Conversation",
    description: "",
    endNodeType: EndNodeType.ArchiveConversation,
    archiveReason: "",
    completionStatus: CompletionStatus.Completed,
  },
};

const archiveConversationTemplate = [
  {
    name: "name",
    label: "Name",
    type: "text",
  },
  {
    name: "description",
    label: "Description",
    type: "textArea",
    style: { height: "50px" },
  },
  {
    name: "archiveReason",
    label: "Reason For Archiving",
    type: "select",
    options: [
      { id: 0, value: "", label: "(None selected)" },
      {
        id: 1,
        value: ArchiveReason.Resolved,
        label: ArchiveReason.Resolved,
      },
      {
        id: 2,
        value: ArchiveReason.TimedOut,
        label: ArchiveReason.TimedOut,
      },
      {
        id: 3,
        value: ArchiveReason.Manual,
        label: ArchiveReason.Manual,
      },
      {
        id: 4,
        value: ArchiveReason.Error,
        label: ArchiveReason.Error,
      },
      {
        id: 5,
        value: ArchiveReason.Other,
        label: ArchiveReason.Other,
      },
    ],
  },
  // {
  //   name: "completionStatus",
  //   label: "Status",
  //   type: "select",
  //   options: [
  //     { id: 0, value: "", label: "(None selected)" },
  //     {
  //       id: 1,
  //       value: CompletionStatus.Completed,
  //       label: CompletionStatus.Completed,
  //     },
  //     {
  //       id: 2,
  //       value: CompletionStatus.Failed,
  //       label: CompletionStatus.Failed,
  //     },
  //     {
  //       id: 3,
  //       value: CompletionStatus.Cancelled,
  //       label: CompletionStatus.Cancelled,
  //     },
  //     {
  //       id: 4,
  //       value: CompletionStatus.Paused,
  //       label: CompletionStatus.Paused,
  //     },
  //   ],
  // },
];

const archiveConversationNodeSchema = {
  name: Joi.string().required(),
  description: Joi.string().allow(null, ""),
  endNodeType: Joi.string().required(),
  archiveReason: Joi.string().required(),
  completionStatus: Joi.string().required().label("Completion Status"),
};

const defaultWebhookNotificationNode: WebhookNotificationNode = {
  nodeType: "end",
  settings: {
    name: "Webhook Notification",
    description: "",
    endNodeType: EndNodeType.WebhookNotification,
    maxRetries: 0,
    retryDelayMs: 0,
    webhookList: [],
    completionStatus: CompletionStatus.Completed,
  },
};

const webhookNotificationNodeTemplate = [
  {
    name: "name",
    label: "Name",
    type: "text",
  },
  {
    name: "description",
    label: "Description",
    type: "textArea",
    style: { height: "50px" },
  },
  {
    name: "maxRetries",
    label: "Maximum No of Retries",
    type: "number",
  },
  {
    name: "retryDelayMs",
    label: "Retry Delay",
    type: "number",
  },
  // {
  //   name: "completionStatus",
  //   label: "Status",
  //   type: "select",
  //   options: [
  //     { id: 0, value: "", label: "(None selected)" },
  //     {
  //       id: 1,
  //       value: CompletionStatus.Completed,
  //       label: CompletionStatus.Completed,
  //     },
  //     {
  //       id: 2,
  //       value: CompletionStatus.Failed,
  //       label: CompletionStatus.Failed,
  //     },
  //     {
  //       id: 3,
  //       value: CompletionStatus.Cancelled,
  //       label: CompletionStatus.Cancelled,
  //     },
  //     {
  //       id: 4,
  //       value: CompletionStatus.Paused,
  //       label: CompletionStatus.Paused,
  //     },
  //   ],
  // },
];

const webhookNotificationNodeSchema = {
  name: Joi.string().required(),
  description: Joi.string().allow(null, ""),
  endNodeType: Joi.string().required(),
  maxRetries: Joi.number().allow(null, ""),
  retryDelayMs: Joi.number().allow(null, ""),
  webhookList: Joi.array().items().min(1).label("Webhook"),
  completionStatus: Joi.string().required().label("Completion Status"),
};

export {
  CompletionStatus,
  EndNode,
  EndNodeSettings,
  defaultEndNodeSettings,
  defaultEndNode,
  EndNodeType,
  endNodeSettingsTemplate,
  endNodeSettingsSchema,
  ArchiveConversationNode,
  ArchiveConversationNodeSettings,
  defaultArchiveConversationNode,
  archiveConversationNodeSchema,
  archiveConversationTemplate,
  WebhookNotificationNode,
  WebhookNotificationNodeSettings,
  defaultWebhookNotificationNode,
  webhookNotificationNodeSchema,
  webhookNotificationNodeTemplate,
};
